import { useEffect, useState } from "react";
import { fetchOverview, ApiError } from "../api/client.js";
import type { OverviewStats } from "@atlas/shared/types";

/** Risk color card for the overview grid */
function StatCard({
  label,
  value,
  color,
}: {
  label: string;
  value: number | string | null;
  color: "green" | "amber" | "red" | "gray" | "blue";
}) {
  const colors = {
    green: "border-green-600 text-green-400",
    amber: "border-amber-500 text-amber-400",
    red: "border-red-600 text-red-400",
    gray: "border-gray-600 text-gray-400",
    blue: "border-blue-600 text-blue-400",
  };

  return (
    <div className={`border rounded-lg p-4 ${colors[color]} bg-gray-900`}>
      <div className="text-2xl font-bold">{value ?? "—"}</div>
      <div className="text-xs text-gray-500 mt-1">{label}</div>
    </div>
  );
}

export default function OverviewPage() {
  const [stats, setStats] = useState<OverviewStats | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  function load() {
    setLoading(true);
    setError(null);
    fetchOverview()
      .then(setStats)
      .catch((e: unknown) => {
        setError(e instanceof ApiError ? e.message : "Failed to load overview");
      })
      .finally(() => setLoading(false));
  }

  useEffect(load, []);

  if (loading) return <div className="text-gray-500 text-sm">Loading…</div>;
  if (error) return <div className="text-red-400 text-sm">Error: {error}</div>;
  if (!stats) return null;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Overview</h1>
        <button
          onClick={load}
          disabled={loading}
          className="text-sm px-3 py-1.5 rounded bg-gray-800 hover:bg-gray-700 disabled:opacity-50 text-white transition-colors"
        >
          {loading ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Active Domains" value={stats.activeDomains} color="blue" />
        <StatCard label="Expiring ≤30d" value={stats.expiringSoon} color={stats.expiringSoon > 0 ? "amber" : "green"} />
        <StatCard label="Expired" value={stats.expired} color={stats.expired > 0 ? "red" : "green"} />
        <StatCard label="Scan Failures" value={stats.scanFailures} color={stats.scanFailures > 0 ? "red" : "green"} />
      </div>

      <div>
        <h2 className="text-sm font-medium text-gray-400 mb-3">CA/Browser Forum Compliance (SC-081)</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Compliant" value={stats.complianceSummary.compliant} color="green" />
          <StatCard label="Non-compliant" value={stats.complianceSummary.non_compliant} color="red" />
          <StatCard label="Legacy" value={stats.complianceSummary.legacy} color="amber" />
          <StatCard label="Unknown" value={stats.complianceSummary.unknown} color="gray" />
        </div>
      </div>

      {stats.lastScanAt && (
        <p className="text-xs text-gray-500">
          Last scan: {new Date(stats.lastScanAt).toLocaleString()}
        </p>
      )}
    </div>
  );
}
